import { useEffect } from 'react';
import { PENDING_COUPON_INFO, validateDiscountCode } from 'lib/billing';
import { useCheckoutStore } from './useCheckoutStore';
import type { CouponInfo } from './types';

/**
 * Owns discount-code verification against the public billing REST endpoint
 * (GET /public/api/v1/billing/discount-code) and writes the verdict into
 * `useCheckoutStore.couponInfo`. Mount once near the top of the checkout tree,
 * next to useInvoicePreviewSync (CheckoutProvider).
 *
 * Fires only when a code is actually applied — the field has an explicit
 * Apply button, so this is per-apply (and on mount with a code already set),
 * never per keystroke. Latest wins: the applied code changing aborts the
 * in-flight request and setCoupon already blanked couponInfo, so a stale
 * response can never describe a newer code.
 *
 * Verdicts (see lib/billing/discountCode):
 *   'pending'           — round-trip in flight (set synchronously below, so
 *                         the UI can show progress instead of the fail-open
 *                         optimistic note)
 *   'valid' / 'invalid' — the backend actually answered for this code
 *   'unknown'           — network failure, or base URL unconfigured
 *                         (REACT_APP_PUBLIC_BILLING_API unset) — fail-open
 * 'unknown' (and null) leave the TotalSummary feedback exactly on its
 * preview-inference behavior; the invoice preview stays authoritative for
 * discounted amounts in every case.
 */
export function useCouponValidationSync(): void {
  const coupon = useCheckoutStore((s) => s.coupon);
  const setCouponInfo = useCheckoutStore((s) => s.setCouponInfo);

  useEffect(() => {
    if (!coupon) {
      // No applied code — nothing to verify. setCoupon already cleared the
      // verdict; clearing again keeps the hook correct standalone.
      setCouponInfo(null);
      return;
    }

    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    // pending (not unknown) while the round-trip is in flight — twin of the
    // marketing checkout's validation effect. Every settled outcome below
    // overwrites it; network failure still resolves 'unknown' (fail-open).
    setCouponInfo(PENDING_COUPON_INFO as CouponInfo);

    (async () => {
      // Never throws: aborts/transport failures resolve to status 'unknown'.
      const info = (await validateDiscountCode(coupon, { signal: controller?.signal })) as CouponInfo;
      if (!cancelled) setCouponInfo(info);
    })();

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [coupon, setCouponInfo]);
}
